import { useRef, useState } from "react";
import { Download, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, apiError, downloadFile } from "@/lib/api";

const btn = "h-11 rounded-none font-display text-[11px] font-bold uppercase tracking-widest";

const Preview = ({ preview }) => (
  <div className="max-h-[50vh] space-y-3 overflow-y-auto text-xs">
    <div className="flex flex-wrap gap-4 font-bold">
      <span data-testid="sheet-changes-count">Changed · {preview.changes.length}</span>
      <span data-testid="sheet-added-count">New · {preview.added.length}</span>
      <span data-testid="sheet-errors-count" className={preview.errors.length ? "text-destructive" : ""}>
        Rows refused · {preview.errors.length}
      </span>
    </div>
    {preview.changes.map((c) => (
      <div key={c.row} className="border-l-2 border-primary bg-muted/40 px-3 py-2" data-testid={`sheet-change-${c.row}`}>
        <b>Row {c.row} · {c.name}</b>
        {c.fields.map((f) => (
          <div key={f.field} className="font-data text-muted-foreground">
            {f.field}: {String(f.from || "blank")} → <span className="text-foreground">{String(f.to || "blank")}</span>
          </div>
        ))}
      </div>
    ))}
    {preview.added.map((a) => (
      <div key={a.row} className="border-l-2 border-[hsl(var(--healthy))] bg-muted/40 px-3 py-2" data-testid={`sheet-added-${a.row}`}>
        <b>Row {a.row} · {a.name}</b> — new, {a.team_role} ({a.team}{a.home ? `, ${a.home}` : ""})
      </div>
    ))}
    {preview.errors.map((e) => (
      <div key={e.row} className="border-l-2 border-destructive px-3 py-2 text-destructive" data-testid={`sheet-error-${e.row}`}>
        Row {e.row}: {e.why}
      </div>
    ))}
    {!preview.changes.length && !preview.added.length && !preview.errors.length && (
      <p className="text-muted-foreground">Nothing in this file differs from the portal.</p>
    )}
  </div>
);

/** The People sheet — one row per person, out and back in, with every change shown first. */
export const PeopleSheet = ({ canUpload, onApplied }) => {
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");

  const download = async () => {
    setBusy("down");
    try {
      await downloadFile("/people/sheet", {}, "SAIL_People.xlsx");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const post = async (f, apply) => {
    const fd = new FormData();
    fd.append("file", f);
    return (await api.post("/people/sheet", fd, { params: { apply } })).data;
  };

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy("read");
    try {
      setPreview(await post(f, false));
      setFile(f);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy("");
    }
  };

  const apply = async () => {
    setBusy("save");
    try {
      const res = await post(file, true);
      toast.success(`${res.saved} person(s) saved from the sheet.`);
      setPreview(null);
      onApplied();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy("");
    }
  };

  const count = preview ? preview.changes.length + preview.added.length : 0;
  return (
    <>
      <Button variant="outline" className={btn} onClick={download} disabled={!!busy} data-testid="people-sheet-download">
        {busy === "down" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
        People sheet
      </Button>
      {canUpload && (
        <>
          <Button variant="outline" className={btn} onClick={() => input.current?.click()} disabled={!!busy} data-testid="people-sheet-upload">
            {busy === "read" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
            Upload sheet
          </Button>
          <input ref={input} type="file" accept=".xlsx" className="hidden" onChange={pick} data-testid="people-sheet-file" />
        </>
      )}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl rounded-none" data-testid="people-sheet-preview">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black">What this sheet changes</DialogTitle>
            <DialogDescription>Nothing is saved until you confirm. Refused rows are left out.</DialogDescription>
          </DialogHeader>
          {preview && <Preview preview={preview} />}
          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <Button variant="outline" className="rounded-none" onClick={() => setPreview(null)} data-testid="people-sheet-cancel">
              Cancel
            </Button>
            <Button className="rounded-none" disabled={!count || busy === "save"} onClick={apply} data-testid="people-sheet-confirm">
              {busy === "save" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save {count} change(s)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
